import { ActivityInput, Domain } from '../../../graphql/generated/graphql.ts';
import { FC, useEffect, useState } from 'react';
import { Box, Button, Checkbox, Collapse, Divider, FormControlLabel, FormGroup, Paper, Stack, Typography } from '@mui/material';
import { ActivityDisplay } from '../../../components/activity-planning/activity-display.tsx';
import { getDay } from 'date-fns';

export interface TimeSelectionProps {
  domain: Partial<Domain>;
  input: Partial<ActivityInput>;
  onNext: (input: Partial<ActivityInput>) => void;
  flat?: boolean;
}

export type Day = 'Saturday' | 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday';
export type Period = 'Morning' | 'Afternoon' | 'Evening';

export type DayPeriodSelection = {
  day: Day;
  period: Period[];
};

export type WeekdaySelectionInputProps = {
  handleDaySelection: (selectedDay: Day) => void;
  handlePeriodSelection: (selectedDay: Day, selectedPeriod: Period) => void;
};

export const TimeSelection: FC<TimeSelectionProps> = ({ domain, input, onNext, flat }) => {
  const [schedule, setSchedule] = useState<DayPeriodSelection[]>([]);
  const [selectedDays, setSelectedDays] = useState<{ [key: string]: boolean }>({
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
    Sunday: false
  });

  const [selectedTimes, setSelectedTimes] = useState<{ [key: string]: { Morning: boolean; Afternoon: boolean; Evening: boolean } }>({
    Monday: { Morning: false, Afternoon: false, Evening: false },
    Tuesday: { Morning: false, Afternoon: false, Evening: false },
    Wednesday: { Morning: false, Afternoon: false, Evening: false },
    Thursday: { Morning: false, Afternoon: false, Evening: false },
    Friday: { Morning: false, Afternoon: false, Evening: false },
    Saturday: { Morning: false, Afternoon: false, Evening: false },
    Sunday: { Morning: false, Afternoon: false, Evening: false }
  });

  useEffect(() => {
    if (input.schedule) {
      const updatedDays: { [key: string]: boolean } = { ...selectedDays };
      const updatedTimes: { [key: string]: { Morning: boolean; Afternoon: boolean; Evening: boolean } } = { ...selectedTimes };
      input.schedule.forEach((item) => {
        updatedDays[item.day] = true;
        item.period.forEach((period) => {
          // @ts-ignore
          updatedTimes[item.day][period] = true;
        });
      });
      setSelectedDays(updatedDays);
      setSelectedTimes(updatedTimes);
      // @ts-ignore
      setSchedule(input.schedule);
    }
  }, [input.schedule]);

  const handleDayChange = (day: string) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const handleTimeChange = (day: string, time: keyof (typeof selectedTimes)[typeof day]) => {
    setSelectedTimes((prev) => ({
      ...prev,
      [day]: { ...prev[day], [time]: !prev[day][time] }
    }));
  };

  const handleDaySelection = async (selectedDay: Day) => {
    setSchedule((prevSchedule) => {
      const newSchedule = [...prevSchedule];
      const day = newSchedule.find((item) => item.day === selectedDay);
      if (day) {
        return newSchedule.filter((item) => item.day !== selectedDay);
      } else {
        return [...newSchedule, { day: selectedDay, period: [] }];
      }
    });
  };

  const handlePeriodSelection = (selectedDay: Day, selectedPeriod: Period) => {
    setSchedule((prevSchedule) => {
      const newSchedule = [...prevSchedule];
      const day = newSchedule.find((item) => item.day === selectedDay);
      if (day) {
        if (day.period.includes(selectedPeriod)) {
          day.period = day.period.filter((period) => period !== selectedPeriod);
          if (day.period.length === 0) {
            return newSchedule.filter((item) => item.day !== selectedDay);
          }
        } else {
          day.period = [...day.period, selectedPeriod];
        }
      } else {
        newSchedule.push({ day: selectedDay, period: [selectedPeriod] });
      }
      return newSchedule;
    });
  };

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const today = getDay(new Date());
  return (
    <Stack spacing={2}>
      {!flat && <ActivityDisplay activity={input} domain={domain} />}
      <Typography variant="h6" sx={{ my: 2 }}>
        Almost there! Now, let's set the schedule for your activity.
      </Typography>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto'
        }}
      >
        {weekDays.map((day, index) => (
          <Stack key={day} spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={
                    index + 1 < today ||
                    (index + 1 === today && new Date().getHours() >= 20) ||
                    selectedTimes[day].Morning ||
                    selectedTimes[day].Afternoon ||
                    selectedTimes[day].Evening
                  } // Disable if the day has already passed (after 8 PM)
                  checked={selectedDays[day]}
                  onChange={() => {
                    handleDaySelection(day as Day);
                    handleDayChange(day);
                  }}
                />
              }
              label={day}
            />
            <Collapse in={selectedDays[day]}>
              <Stack spacing={1} sx={{ pl: 3 }}>
                <Paper sx={{ p: 2 }} variant="outlined">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTimes[day].Morning}
                          disabled={index + 1 < today}
                          onChange={() => {
                            handlePeriodSelection(day as Day, 'Morning');
                            handleTimeChange(day, 'Morning');
                          }}
                        />
                      }
                      label="Morning"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTimes[day].Afternoon}
                          disabled={index + 1 < today}
                          onChange={() => {
                            handlePeriodSelection(day as Day, 'Afternoon');
                            handleTimeChange(day, 'Afternoon');
                          }}
                        />
                      }
                      label="Afternoon"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTimes[day].Evening}
                          disabled={index + 1 < today}
                          onChange={() => {
                            handlePeriodSelection(day as Day, 'Evening');
                            handleTimeChange(day, 'Evening');
                          }}
                        />
                      }
                      label="Evening"
                    />
                  </FormGroup>
                </Paper>
              </Stack>
            </Collapse>
          </Stack>
        ))}
      </Box>
      <Button
        fullWidth
        variant="contained"
        onClick={() => {
          onNext({
            ...input,
            schedule
          });
        }}
      >
        Set Schedule
      </Button>
    </Stack>
  );
};
